import { FC, useMemo } from 'react'
import styled from '@emotion/styled'
import { ModalCard } from 'src/v2/views/owner-reveal/insights/parcels/parcel-modal/modal-card'
import { DescriptionItem } from 'src/v2/components/description-item'
import { useAtomValue } from 'jotai'
import * as atoms from 'src/v2/state/owner-reveal/entity-parcels'
import { sortDebts, sortSales, sortTaxes } from 'src/utils/property-records'
import { formatVal } from 'src/utils/number-format'
import { formatDateMMMMDDYYYY } from 'src/utils/date-utils'
import { useGetParcelLinks } from 'src/v2/hooks/use-get-parcel-links'

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Row = styled.div`
  display: flex;
  gap: 16px;
`
const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

export const FinancesCard: FC = () => {
  const data = useAtomValue(atoms.selectedParcelPropertyRecords)
  const parcel = useAtomValue(atoms.selectedParcel)
  const { getParcelLinks, notFound: disableActionButton } = useGetParcelLinks(parcel)

  const recentSale = useMemo(() => {
    if (!data) {
      return
    }
    const sortedSales = sortSales(data.sales)
    return sortedSales[0]
  }, [data])

  const recentTax = useMemo(() => {
    if (!data) {
      return
    }
    const sortedTaxes = sortTaxes(data.taxes)
    return sortedTaxes[0]
  }, [data])

  const recentDebt = useMemo(() => {
    if (!data) {
      return
    }
    const sortedDebts = sortDebts(data.debts)
    return sortedDebts[0]
  }, [data])

  const openPropertyRecordsPage = async () => {
    const links = await getParcelLinks()
    if (links?.propertyRecordsLink) {
      window.open(links.propertyRecordsLink, '_blank')
    }
  }

  return (
    <ModalCard
      title="Finances"
      actionText="Property Records"
      onActionClick={openPropertyRecordsPage}
      actionDisabled={disableActionButton}>
      <Content>
        <Row>
          <Column>
            <DescriptionItem label="RECENT SALE DATE" value={recentSale?.recordedDate} />
          </Column>
          <Column>
            <DescriptionItem label="RECENT BUYER" value={recentSale?.buyer} />
          </Column>
        </Row>
        <Row>
          <Column>
            <DescriptionItem label="RECENT SALE PRICE" value={formatVal(recentSale?.salePrice, 'currency-long')} />
          </Column>
          <Column>
            <DescriptionItem label="MORTGAGE AMOUNT" value={formatVal(recentDebt?.amount, 'currency-long')} />
          </Column>
        </Row>
        <Row>
          <Column>
            <DescriptionItem
              label="ORIGINATION DATE"
              value={recentDebt?.date ? formatDateMMMMDDYYYY(recentDebt?.date) : undefined}
            />
          </Column>
          <Column>
            <DescriptionItem
              label="MATURITY DATE"
              value={recentDebt?.maturityDate ? formatDateMMMMDDYYYY(recentDebt?.maturityDate) : undefined}
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <DescriptionItem label="LENDER NAME" value={recentDebt?.lender} />
          </Column>
          <Column>
            <DescriptionItem
              label="TAX AMOUNT"
              value={`${formatVal(recentTax?.taxAmount, 'currency-long')}${recentTax?.taxYear ? ` (${recentTax.taxYear})` : ''}`}
            />
          </Column>
        </Row>
      </Content>
    </ModalCard>
  )
}
